import { ReactNode, useId, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

interface SectionPaperProps {
  title: string;
  count?: number;
  children: ReactNode;
  defaultExpanded?: boolean;
}

export function SectionPaper({
  title,
  count,
  children,
  defaultExpanded = false,
}: SectionPaperProps) {
  const baseId = useId();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const heading = `${title}${count !== undefined ? ` (${count})` : ""}`;
  const panelId = `${baseId}-panel`;
  const headerId = `${baseId}-header`;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
      disableGutters
      elevation={0}
      slotProps={{
        transition: { unmountOnExit: true },
      }}
      sx={{
        mb: 3,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        "&:before": { display: "none" },
        boxShadow: "none",
        bgcolor: "background.paper",
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMore sx={{ color: "action.active", fontSize: "1.5rem" }} />
        }
        aria-controls={panelId}
        id={headerId}
        sx={{
          px: 2,
          py: 1.5,
          minHeight: 56,
          "& .MuiAccordionSummary-content": { my: 1 },
        }}
      >
        <Typography
          component="span"
          variant="h3"
          sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" }, fontWeight: 600 }}
        >
          {heading}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        id={panelId}
        aria-labelledby={headerId}
        sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}
